import React from "react";
import { AlertTriangle, CheckCircle2, Utensils, Activity, XCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import AppointmentCTA from "../components/AppointmentCTA";
import FAQSection from "../components/FAQSection";
import { images, fattyLiverFaqs, clinic } from "../data/mock";

const grades = [
  { title: "Grade 1 (Mild)", desc: "Small amount of fat in liver cells. Usually reversible with diet and lifestyle changes.", color: "bg-amber-50 border-amber-200 text-amber-700" },
  { title: "Grade 2 (Moderate)", desc: "More noticeable fat accumulation. Needs medical guidance alongside lifestyle correction.", color: "bg-orange-50 border-orange-200 text-orange-700" },
  { title: "Grade 3 (Severe)", desc: "Significant fat buildup with risk of inflammation and scarring. Requires active treatment.", color: "bg-red-50 border-red-200 text-red-700" },
];

const types = [
  { name: "NAFLD", desc: "Non-Alcoholic Fatty Liver Disease — fat buildup unrelated to alcohol, often linked to diabetes, obesity and cholesterol." },
  { name: "NASH", desc: "Non-Alcoholic Steatohepatitis — an advanced form with inflammation and liver cell damage requiring close monitoring." },
  { name: "Alcoholic Fatty Liver", desc: "Fat accumulation caused by regular alcohol consumption; reversible in early stages with abstinence and treatment." },
];

const causes = ["Obesity & excess belly fat", "Type 2 Diabetes / Insulin resistance", "High cholesterol & triglycerides", "Excess alcohol consumption", "Sedentary lifestyle", "Junk & high-sugar diet"];
const dietDo = ["Green vegetables & fruits", "Whole grains & fibre", "Lean protein (fish, pulses)", "Regular exercise (30 min/day)", "Plenty of water"];
const dietAvoid = ["Fried & processed foods", "Sugary drinks & sweets", "Refined carbs (maida)", "Excess red meat", "Alcohol"];

const FattyLiver = () => {
  return (
    <div>
      <SEO path="/fatty-liver-treatment-varanasi" faqs={fattyLiverFaqs} />
      <PageHero breadcrumb="Fatty Liver Treatment"
        title="Fatty Liver Treatment in Varanasi"
        subtitle="Expert diagnosis and reversal of Grade 1–3 fatty liver, NAFLD, NASH and alcoholic fatty liver disease." />

      {/* WHAT IS */}
      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading center={false} eyebrow="Overview" title="What is Fatty Liver Disease?" />
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>Fatty liver disease, medically known as hepatic steatosis, occurs when excess fat (more than 5–10% of the liver's weight) accumulates inside the liver cells. It has become one of the most common liver conditions in India, affecting a large share of adults — including many who are not overweight and do not drink alcohol.</p>
              <p>In its early stages fatty liver often causes no symptoms at all, which is why it is frequently discovered by chance during a routine ultrasound, health check-up or blood test showing raised liver enzymes (SGPT/SGOT). This "silent" nature is exactly what makes early screening so valuable.</p>
              <p>The good news is that fatty liver is one of the most treatable and often <strong className="text-slate-800">reversible</strong> liver conditions — especially when caught in Grade 1 or Grade 2. With the right combination of diet, weight management, exercise, control of diabetes and cholesterol, and targeted medical care, most patients can restore healthy liver function.</p>
              <p>If left untreated, however, fatty liver can silently progress to inflammation (NASH), fibrosis and eventually cirrhosis or even liver cancer. Early evaluation with {clinic.doctorName} — using tools like FibroScan — can accurately assess your stage and stop this progression before it becomes dangerous.</p>
            </div>
          </div>
          <div>
            <img src={images.liverStages} alt="Stages of liver damage from healthy to cirrhosis" className="w-full rounded-3xl shadow-xl border border-slate-100 bg-white" />
          </div>
        </div>
      </section>

      {/* GRADES */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Severity" title="Grades of Fatty Liver" subtitle="Understanding your grade helps guide the right treatment." />
          <div className="grid md:grid-cols-3 gap-6">
            {grades.map((g, i) => (
              <div key={i} className={`rounded-2xl p-7 border-2 ${g.color}`}>
                <h3 className="font-heading font-bold text-xl mb-3">{g.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-6">
            {types.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h4 className="font-heading font-bold text-sky-600 mb-2">{t.name}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAUSES */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Causes & Risk Factors" title="What Causes Fatty Liver?" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {causes.map((c, i) => (
              <div key={i} className="flex items-center gap-3 bg-sky-50/60 border border-sky-100 rounded-xl px-5 py-4">
                <AlertTriangle size={20} className="text-sky-500 shrink-0" />
                <span className="text-sm font-medium text-slate-700">{c}</span>
              </div>
            ))}
          </div>
          <p className="text-slate-600 leading-relaxed max-w-4xl mx-auto text-center mt-8">
            Fatty liver is closely linked to metabolic syndrome — a cluster of conditions including obesity, high blood sugar, high blood pressure and abnormal cholesterol. Genetics, certain medicines, rapid weight loss, thyroid problems and PCOS can also contribute. Identifying and treating the underlying cause is the key to lasting recovery.
          </p>
        </div>
      </section>

      {/* SYMPTOMS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Symptoms" title="Signs & Symptoms of Fatty Liver"
            subtitle="Early fatty liver is usually silent — but as it advances, warning signs may appear." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {["Persistent tiredness & fatigue", "Discomfort or dull ache in the upper right abdomen", "Unexplained weight fluctuations", "Feeling of fullness or bloating", "Mild nausea or loss of appetite", "Weakness and reduced concentration"].map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl px-5 py-4 shadow-sm">
                <Activity size={20} className="text-sky-500 shrink-0 mt-0.5" />
                <span className="text-sm font-medium text-slate-700">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIAGNOSIS */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Diagnosis" title="How Fatty Liver is Diagnosed"
            subtitle="Accurate grading guides the right treatment and tracks your progress over time." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { t: "FibroScan", d: "A quick, painless scan that measures liver fat (CAP) and stiffness (fibrosis) with precision." },
              { t: "Ultrasound", d: "The first-line imaging test to detect fat, liver size and structural changes." },
              { t: "LFT (SGPT/SGOT)", d: "Blood tests that reveal liver cell inflammation and how well the liver is working." },
              { t: "Metabolic Panel", d: "Blood sugar, cholesterol and thyroid checks to find the root cause." },
            ].map((d, i) => (
              <div key={i} className="bg-sky-50/60 rounded-2xl p-6 border border-sky-100">
                <span className="font-heading text-sky-500 font-bold text-xl">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-heading font-semibold text-slate-900 mt-2 mb-2">{d.t}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{d.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENT OPTIONS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Treatment Options" title="How Fatty Liver is Treated"
            subtitle="There is no single pill — treatment is a structured, personalized programme." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { t: "Weight & Diet Management", d: "A gradual 7–10% weight reduction can significantly reduce liver fat. A customized, sustainable diet plan forms the foundation of treatment." },
              { t: "Controlling Root Causes", d: "Tight control of diabetes, cholesterol and blood pressure is essential to reverse fatty liver and prevent progression." },
              { t: "Exercise Programme", d: "Regular aerobic activity and strength training help burn liver fat even without major weight loss." },
              { t: "Medical Therapy", d: "Where needed, evidence-based medicines and liver-supportive treatment are added under specialist supervision." },
              { t: "Alcohol & Lifestyle", d: "Complete avoidance of alcohol and correction of harmful habits accelerate recovery." },
              { t: "Regular Monitoring", d: "Periodic FibroScan and blood tests track improvement and fine-tune the plan." },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
                <CheckCircle2 size={26} className="text-sky-500 mb-4" />
                <h3 className="font-heading font-semibold text-slate-900 mb-2">{c.t}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLICATIONS */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <div className="bg-red-50 border border-red-100 rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto">
            <SectionHeading center={false} eyebrow="Warning" title="Complications if Left Untreated" />
            <p className="text-slate-600 leading-relaxed mb-5">Ignoring fatty liver allows it to progress silently through increasingly serious stages:</p>
            <ul className="space-y-3">
              {[
                "NASH (Non-Alcoholic Steatohepatitis) — fat with inflammation and liver cell damage",
                "Liver Fibrosis — scar tissue begins to replace healthy liver",
                "Cirrhosis — permanent, extensive scarring that impairs liver function",
                "Liver Failure & Liver Cancer — the most severe, life-threatening outcomes",
              ].map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 text-sm sm:text-base">
                  <AlertTriangle size={20} className="text-red-500 mt-0.5 shrink-0" /> {c}
                </li>
              ))}
            </ul>
            <p className="text-slate-600 leading-relaxed mt-5">The earlier fatty liver is treated, the higher the chance of complete reversal. Timely consultation is the single most important step you can take.</p>
          </div>
        </div>
      </section>

      {/* DIET */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Diet & Lifestyle" title="Can Fatty Liver Be Reversed?"
            subtitle="Yes — in most early cases. The right diet and lifestyle are the foundation of recovery." />
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-7 border border-green-100 shadow-sm">
              <h3 className="font-heading font-bold text-lg text-green-700 flex items-center gap-2 mb-5"><Utensils size={20} /> Recommended</h3>
              <ul className="space-y-3">{dietDo.map((d, i) => <li key={i} className="flex items-center gap-3 text-slate-700 text-sm"><CheckCircle2 size={18} className="text-green-500 shrink-0" /> {d}</li>)}</ul>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-red-100 shadow-sm">
              <h3 className="font-heading font-bold text-lg text-red-600 flex items-center gap-2 mb-5"><XCircle size={20} /> Avoid</h3>
              <ul className="space-y-3">{dietAvoid.map((d, i) => <li key={i} className="flex items-center gap-3 text-slate-700 text-sm"><XCircle size={18} className="text-red-400 shrink-0" /> {d}</li>)}</ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Why Choose Us" title="Fatty Liver Treatment with Dr. Indresh Dixit" />
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Activity, t: "Accurate Grading", d: "FibroScan and imaging to precisely assess fat and fibrosis levels." },
              { icon: Utensils, t: "Custom Diet Plans", d: "Practical, sustainable diet and lifestyle plans built around your routine." },
              { icon: CheckCircle2, t: "Proven Reversal", d: "Structured follow-up to track your progress toward a healthy liver." },
            ].map((c, i) => (
              <div key={i} className="bg-sky-50/60 rounded-2xl p-7 border border-sky-100">
                <div className="w-12 h-12 rounded-xl bg-sky-500 text-white grid place-items-center mb-5"><c.icon size={24} /></div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{c.t}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={fattyLiverFaqs} title="Fatty Liver — Frequently Asked Questions" />
      <AppointmentCTA title="Reverse Your Fatty Liver — Start Today" />
    </div>
  );
};

export default FattyLiver;
